import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Chip, Checkbox, ListItemText, Snackbar, Alert } from '@mui/material';
import WorkIcon         from '@mui/icons-material/Work';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { FILTERS_UPDATE } from '../../actions/siteActions';

const ActivityTitleFilter = () => {
  const dispatch        = useDispatch();
  const filters         = useSelector((s) => s.sites.filters);
  // [{ title, hasSites, isClosed }]
  const activityTitles  = useSelector((s) => s.sites.activityTitles || []);
  const [open, setOpen] = useState(false);
  const [warn, setWarn] = useState('');
  const ref             = useRef(null);

  const selected      = filters.selectedTitles || [];
  const currentStatus = filters.activityStatus || 'Open';
  const isClosedMode  = currentStatus === 'Closed';

  // Show only titles matching the current legend mode (Open or Closed)
  const visibleTitles = useMemo(
    () => activityTitles.filter((t) => !!t.isClosed === isClosedMode),
    [activityTitles, isClosedMode]
  );

  const toggle = (item) => {
    const title      = item.title;
    const hasSites   = !!item.hasSites;
    const isSelected = selected.includes(title);

    if (!isSelected && !hasSites) {
      setWarn(`"${title}" has no sites linked in the tracker — cannot be selected`);
      return;
    }
    const next = isSelected
      ? selected.filter((t) => t !== title)
      : [...selected, title];
    dispatch({ type: FILTERS_UPDATE, payload: { selectedTitles: next } });
  };

  const clear = () => {
    dispatch({ type: FILTERS_UPDATE, payload: { selectedTitles: [] } });
  };

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (visibleTitles.length === 0) return null;

  return (
    <>
      <Box ref={ref} sx={{ position: 'absolute', top: 56, right: 16, zIndex: 1000 }}>

        <Box onClick={() => setOpen((v) => !v)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            backgroundColor: selected.length > 0 ? '#1565C0' : '#0D1B2A',
            color: '#fff', borderRadius: 2, px: 2, py: 0.9, cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)', border: '1px solid #1E3A5F',
            transition: 'all 0.15s',
            '&:hover': { backgroundColor: '#1565C0', borderColor: '#1565C0' },
          }}>
          <WorkIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#fff', fontFamily: 'inherit' }}>
            Activity
          </Typography>
          {selected.length > 0 && (
            <Chip label={selected.length} size="small"
              sx={{ ml: 0.5, backgroundColor: '#FF6B35', color: '#fff', height: 18, fontSize: 10 }} />
          )}
        </Box>

        {open && (
          <Box sx={{
            position: 'absolute', top: '100%', right: 0, mt: 0.5,
            backgroundColor: '#0D1B2A', border: '1px solid #1E3A5F',
            borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            minWidth: 260, maxWidth: 380, maxHeight: 400, overflowY: 'auto',
            zIndex: 1400,
          }}>
            <Box sx={{
              px: 2, py: 1.2, borderBottom: '1px solid #1E3A5F',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              position: 'sticky', top: 0, backgroundColor: '#0D1B2A',
            }}>
              <Typography sx={{ color: '#90A4AE', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
                {isClosedMode ? 'Closed / Overdue' : 'Open'} Titles ({visibleTitles.length})
              </Typography>
              {selected.length > 0 && (
                <Typography onClick={clear}
                  sx={{ color: '#FF6B35', fontSize: 10, fontWeight: 700, cursor: 'pointer',
                        '&:hover': { color: '#fff' } }}>
                  Clear all
                </Typography>
              )}
            </Box>

            {visibleTitles.map((item) => {
              const title    = item.title;
              const hasSites = !!item.hasSites;
              const active   = selected.includes(title);
              const tickColor = isClosedMode ? '#BF360C' : '#4FC3F7';
              return (
                <Box key={title} onClick={() => toggle(item)}
                  sx={{
                    display: 'flex', alignItems: 'center', px: 1.5, py: 0.5,
                    cursor: 'pointer', borderBottom: '1px solid #0D2A40',
                    opacity: hasSites ? 1 : 0.5,
                    '&:hover': { backgroundColor: '#0D2A40' },
                  }}>
                  <Checkbox size="small" checked={active} disabled={!hasSites && !active}
                    sx={{ p: 0.5, color: '#1E3A5F', '&.Mui-checked': { color: tickColor } }} />
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        {!hasSites && (
                          <WarningAmberIcon sx={{ fontSize: 12, color: '#FFB300' }} />
                        )}
                        <Typography sx={{
                          fontSize: 12,
                          color: active ? '#E0E7FF' : hasSites ? '#90A4AE' : '#FFB300',
                          fontFamily: '"IBM Plex Mono", monospace',
                          fontWeight: active ? 700 : 400,
                        }}>
                          {title}
                        </Typography>
                      </Box>
                    }
                    secondary={!hasSites ? 'No sites linked in tracker' : null}
                    secondaryTypographyProps={{
                      fontSize: 10, color: '#FFB300',
                      fontFamily: '"IBM Plex Mono", monospace',
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Snackbar
        open={!!warn}
        autoHideDuration={5000}
        onClose={() => setWarn('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setWarn('')} severity="warning" variant="filled"
          sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: 12 }}>
          {warn}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ActivityTitleFilter;